import { Box, Typography } from '@mui/material'
import { PurchaseLayout } from 'hoc/PurchaseLayout'
import React, { useEffect, useState } from 'react'
import { PurchasePlansDetailFilters } from './PurchasePlansDetailFilters'
import styled from '@emotion/styled'
import { PurchasePlansDetailTable } from './PurchasePlansDetailTable'
import { ArrowBack } from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import { useProcurementPlanItemsSearchQuery } from 'services/procurementPlans/procurementPlanItems'
import { useQueryState } from 'react-router-use-location-state'
import { CustomPagination } from 'components/purchase/CustomPagination'
import { EmptyTableState } from 'components/purchase/EmptyTableState'
import {
  useProcurementPlanQuery,
  useProcurementPlansRelationsQuery,
} from 'services/procurementPlans'
import { IBreadcrumb } from 'types/breadcrumb'
import { formatTimeStampToStandartDate } from 'hooks/date/formattedTime'
import { useTranslation } from 'react-i18next'
import { Loading } from 'components/Loading'
import { Error } from 'components/Error'

const breadCrumbItems: IBreadcrumb[] = [
  {
    id: 'home',
    title: 'Ana səhifə',
    path: '/',
  },
  {
    id: 'procurement_plans',
    title: 'Satınalma planları',
    path: '/purchase-plans',
  },
]

const StyledBox = styled(Box)({
  padding: '4px 8px',
  borderRadius: '6px',
  fontSize: '12px',
  background: 'rgba(3, 34, 76, 0.12)',
  color: 'rgb(3, 34, 76)',
  cursor: 'pointer',
  display: 'inline-block',
  marginRight: '5px',
})

export const PurchasePlansDetail: React.FC = () => {
  const [page, setPage] = useQueryState('page', 0)
  const rowsPerPage = 10
  const [name, setName] = useState('')
  const [breadCrumbs, setBreadCrums] = useState(breadCrumbItems)
  const navigate = useNavigate()
  const { id } = useParams()
  const { t } = useTranslation()

  const args = {
    page,
    size: rowsPerPage,
    name,
    id,
  } as const

  const {
    data: procurementPlanItem,
    isLoading,
    isError,
  } = useProcurementPlanItemsSearchQuery(args, { skip: !id })

  const {
    data: procurementPlan,
    isLoading: isLoadingProcurementPlan,
    isError: isErrorProcurementPlan,
  } = useProcurementPlanQuery({ id: id ?? '' }, { skip: !id })

  const {
    data: correlations,
    isLoading: isCorrelationLoading,
    isError: isCorrelationError,
  } = useProcurementPlansRelationsQuery(
    { id: procurementPlan?.id ?? 0 },
    {
      skip: !procurementPlan?.id,
    }
  )

  useEffect(() => {
    if (procurementPlan) {
      setBreadCrums([
        ...breadCrumbItems,
        { title: procurementPlan?.organizationName! },
      ])
    }
    return () => {
      setBreadCrums(breadCrumbItems)
    }
  }, [procurementPlan, id])

  const handleClickVersions = (paramId: string) => {
    if (id !== paramId) navigate(`/purchase-plans/${paramId}`)
  }

  if (isLoading || isCorrelationLoading || isLoadingProcurementPlan) {
    return <Loading fullPage={false} />
  }
  if (isError || isCorrelationError || isErrorProcurementPlan) {
    return <Error fullPage={false} />
  }
  if (
    correlations === undefined ||
    procurementPlan === undefined ||
    procurementPlanItem === undefined
  ) {
    return <></>
  }

  return (
    <>
      <PurchaseLayout breadCrumbItems={breadCrumbs}>
        <Box
          sx={{
            marginBottom: { xs: '16px', md: '32px' },
            display: 'flex',
            gap: '10px',
          }}
        >
          <ArrowBack
            sx={{ fontSize: '32px', cursor: 'pointer' }}
            onClick={() => navigate('/purchase-plans')}
          />
          <Typography variant="h5">{procurementPlan?.organizationName}</Typography>
        </Box>
        {/*TODO commented on request on 14 Nov*/}
        {/*{correlations.length > 0 &&*/}
        {/*  correlations?.map((version) => {*/}
        {/*    return (*/}
        {/*      <StyledBox*/}
        {/*        onClick={() => handleClickVersions(version.id)}*/}
        {/*      >{`${version.status === 'PUBLISHED' ? t('current') : t('modified')} - ${formatTimeStampToStandartDate(version.publishedAt)}`}</StyledBox>*/}
        {/*    )*/}
        {/*  })}*/}

        <Typography variant="h6" sx={{ color: 'unset', marginTop: '24px' }}>
          <b>{t('changeReason')}:</b> {procurementPlan?.reasonDescription}
        </Typography>
        <PurchasePlansDetailFilters setFilter={setName} />
        {procurementPlanItem?.items && procurementPlanItem?.items.length !== 0 ? (
          <PurchasePlansDetailTable data={procurementPlanItem.items} />
        ) : (
          <EmptyTableState />
        )}
        {procurementPlanItem?.info.totalRows !== 0 && (
          <CustomPagination
            total={procurementPlanItem?.info.totalRows!}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={setPage}
          />
        )}
      </PurchaseLayout>
    </>
  )
}
